import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Icon } from '@iconify/react';
import { clientDashboardService, DashboardData } from '@/services/clientDashboardService';
import invoiceService from '@/services/invoiceService';
import { InvoicesApiResponse } from '@/types/invoice';
import AgendaTerdekatCard from '@/components/client/mobile/AgendaTerdekatCard';
import UnpaidPaymentCard from '@/components/client/mobile/UnpaidPaymentCard';
import MMRLeaderboardCard from '@/components/client/mobile/MMRLeaderboardCard';
import RaporEventCard from '@/components/client/mobile/RaporEventCard';
import QuickActionGrid from '@/components/client/mobile/QuickActionGrid';
import MabarTerdekatList from '@/components/client/mobile/MabarTerdekatList';
import GuestLoginBanner from '@/components/client/mobile/GuestLoginBanner';
import { DashboardClientSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import { useAuth } from '@/contexts/AuthContext';
import { useScrollContainer } from '@/contexts/ScrollContainerContext';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';
import { hostPaths } from '@/utils/hostPaths';

const ClientDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isCommunityUser = user?.role === 'community';
  const [loading, setLoading] = useState(isAuthenticated && !isCommunityUser);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [dataInvoice, setDataInvoice] = useState<InvoicesApiResponse | null>(null);
  const [unpaidMabarEvents, setUnpaidMabarEvents] = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const scrollRef = useScrollContainer();

  useEffect(() => {
    if (isCommunityUser) {
      navigate(hostPaths.home, { replace: true });
    }
  }, [isCommunityUser, navigate]);

  const fetchDashboard = useCallback(async (silent = false) => {
    if (!isAuthenticated || isCommunityUser) {
      setLoading(false);
      return;
    }
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await clientDashboardService.getClientDashboard();

      if (response.success) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to load dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isCommunityUser]);

  const fetchUnpaidEvents = useCallback(async () => {
    if (!isAuthenticated || isCommunityUser) return;
    try {
      const res = await clientDashboardService.getUnpaidEvents();
      if (res.success && Array.isArray(res.data)) {
        const mapped = res.data.map((item) => ({
          id: item.id || item.order_number,
          eventName: item.event_title || `Order #${item.order_number}`,
          dateStr: item.event_date || '',
          venueName: item.lokasi_arena || '',
          amount: item.amount || 0,
          paymentUrl: item.xendit_invoice_url,
          orderNumber: item.order_number,
        }));
        setUnpaidMabarEvents(mapped);
      }
    } catch {
      // Guest / network — ignore unpaid strip
    }
  }, [isAuthenticated, isCommunityUser]);

  const fetchInvoices = useCallback(async () => {
    if (!isAuthenticated || isCommunityUser) return;
    try {
      const response = await invoiceService.getInvoicesByCustomer(1, 5);
      if (response.success) {
        setDataInvoice(response);
      }
    } catch {
      // Guest / network — ignore invoices
    }
  }, [isAuthenticated, isCommunityUser]);

  const refreshHome = useCallback(async () => {
    if (!isAuthenticated || isCommunityUser) {
      setRefreshKey((key) => key + 1);
      return;
    }
    await Promise.all([fetchDashboard(true), fetchUnpaidEvents(), fetchInvoices()]);
    setRefreshKey((key) => key + 1);
  }, [isAuthenticated, isCommunityUser, fetchDashboard, fetchUnpaidEvents, fetchInvoices]);

  const { refreshing } = usePullToRefresh({
    onRefresh: refreshHome,
    enabled: true,
    scrollRef: scrollRef ?? undefined,
  });

  useEffect(() => {
    void fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    void fetchUnpaidEvents();
  }, [fetchUnpaidEvents]);

  useEffect(() => {
    void fetchInvoices();
  }, [fetchInvoices]);

  const formatSafeIndonesianDate = (d: Date | null | undefined): string => {
    if (!d || !(d instanceof Date) || isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString('id-ID', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const getNearestUpcoming = useCallback((sourceData: DashboardData | null) => {
    if (!sourceData) return null;

    const rawBookings = sourceData.upcoming_bookings?.bookings;
    const bookingItems = (Array.isArray(rawBookings) ? rawBookings : [])
      .map((booking) => {
        try {
          if (!booking) return null;
          const dateOnly =
            typeof booking.booking_date === 'string' ? booking.booking_date.split('T')[0] : '';
          let date: Date;
          if (dateOnly && booking.start_time) {
            const localIso = `${dateOnly}T${booking.start_time}`;
            date = new Date(localIso);
            if (isNaN(date.getTime())) {
              date = new Date(booking.booking_date);
            }
          } else if (booking.booking_date) {
            date = new Date(booking.booking_date);
          } else {
            return null;
          }

          if (!(date instanceof Date) || isNaN(date.getTime())) return null;

          let endDate: Date;
          if (dateOnly && booking.end_time) {
            const localEndIso = `${dateOnly}T${booking.end_time}`;
            endDate = new Date(localEndIso);
            if (isNaN(endDate.getTime())) {
              endDate = new Date(date.getTime() + 2 * 60 * 60 * 1000);
            }
          } else {
            endDate = new Date(date.getTime() + 2 * 60 * 60 * 1000);
          }

          const time = formatLocalEventTimeRange(booking.start_time, booking.end_time);

          return {
            type: 'booking' as const,
            data: booking,
            date,
            endDate,
            time,
            title: booking.arena_name || 'Booking Lapangan',
            status: booking.payment_status || 'Terkonfirmasi',
          };
        } catch {
          return null;
        }
      })
      .filter(
        (item): item is NonNullable<typeof item> =>
          item !== null &&
          item.date instanceof Date &&
          !isNaN(item.date.getTime()) &&
          item.endDate instanceof Date &&
          !isNaN(item.endDate.getTime())
      );

    const rawEvents = sourceData.upcoming_events;
    const eventItems = (Array.isArray(rawEvents) ? rawEvents : [])
      .map((event) => {
        try {
          if (!event) return null;
          const dateOnly =
            typeof event.date === 'string'
              ? event.date.includes('T')
                ? event.date.split('T')[0]
                : event.date.split(' ')[0]
              : '';

          let date: Date;
          if (
            event.start_time &&
            !isNaN(new Date(event.start_time).getTime()) &&
            event.start_time.includes('T')
          ) {
            date = new Date(event.start_time);
          } else if (dateOnly && event.start_time) {
            const localIso = `${dateOnly}T${event.start_time}`;
            date = new Date(localIso);
            if (isNaN(date.getTime()) && event.date) {
              date = new Date(event.date);
            }
          } else if (event.date) {
            date = new Date(event.date);
          } else {
            return null;
          }

          if (!(date instanceof Date) || isNaN(date.getTime())) return null;

          let endDate: Date;
          if (
            event.end_time &&
            !isNaN(new Date(event.end_time).getTime()) &&
            event.end_time.includes('T')
          ) {
            endDate = new Date(event.end_time);
          } else if (dateOnly && event.end_time) {
            const localEndIso = `${dateOnly}T${event.end_time}`;
            endDate = new Date(localEndIso);
            if (isNaN(endDate.getTime())) {
              endDate = new Date(date.getTime() + 2 * 60 * 60 * 1000);
            }
          } else {
            endDate = new Date(date.getTime() + 2 * 60 * 60 * 1000);
          }

          const time = formatLocalEventTimeRange(event.start_time, event.end_time);

          return {
            type: 'event' as const,
            data: event,
            date,
            endDate,
            time,
            title: event.event_name || 'Event Mabar',
            status: event.status || 'Terkonfirmasi',
          };
        } catch {
          return null;
        }
      })
      .filter(
        (item): item is NonNullable<typeof item> =>
          item !== null &&
          item.date instanceof Date &&
          !isNaN(item.date.getTime()) &&
          item.endDate instanceof Date &&
          !isNaN(item.endDate.getTime())
      );

    const now = Date.now();
    const allItems = [...bookingItems, ...eventItems]
      .filter((item) => item.endDate.getTime() > now)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return allItems.length > 0 ? allItems[0] : null;
  }, []);

  const upcomingData = useMemo(() => {
    try {
      const nearestItem = getNearestUpcoming(data);
      if (!nearestItem) return null;

      const itemData = nearestItem.data as
        | {
            event_name?: string;
            arena_name?: string;
            sport_category?: string;
            sport_category_slug?: string;
            status?: string;
            participant_status?: string;
            my_status?: string;
            role?: string;
            location?: string;
            map_link?: string;
            maps?: { google_maps_url?: string };
            event_slug?: string;
          }
        | null
        | undefined;

      return {
        eventName:
          nearestItem.title || itemData?.event_name || itemData?.arena_name || 'Agenda Terdekat',
        sportCategory: itemData?.sport_category || 'Futsal',
        sportCategorySlug: itemData?.sport_category_slug || undefined,
        status: itemData?.status || nearestItem.status || 'Terkonfirmasi',
        participantStatus:
          itemData?.participant_status || itemData?.my_status || itemData?.role || undefined,
        date: formatSafeIndonesianDate(nearestItem.date),
        startTime: nearestItem.time || '',
        location: itemData?.arena_name || itemData?.location || nearestItem.title || '',
        mapLink: itemData?.map_link || itemData?.maps?.google_maps_url || undefined,
        slug: itemData?.event_slug || undefined,
        rawDate: nearestItem.date,
        rawEndDate: nearestItem.endDate,
      };
    } catch {
      return null;
    }
  }, [data, getNearestUpcoming]);

  const allUnpaidItems = useMemo(() => {
    try {
      const invoiceData = Array.isArray(dataInvoice?.data) ? dataInvoice.data : [];
      const unpaidInvoiceItems = invoiceData
        .filter(
          (inv) =>
            inv &&
            (inv.payment_status?.toLowerCase() === 'unpaid' ||
              inv.payment_status?.toLowerCase() === 'pending')
        )
        .map((inv) => ({
          id: inv.invoice_number,
          eventName: inv.arena_name
            ? `Booking ${inv.arena_name}`
            : `Invoice #${inv.invoice_number}`,
          dateStr: inv.transaction_date || '',
          venueName: inv.arena_name || '',
          amount: typeof inv.total_amount === 'number' ? inv.total_amount : Number(inv.total_amount) || 0,
          orderNumber: inv.invoice_number,
        }));

      const mabarItems = Array.isArray(unpaidMabarEvents) ? unpaidMabarEvents : [];
      return [...mabarItems, ...unpaidInvoiceItems];
    } catch {
      return [];
    }
  }, [dataInvoice, unpaidMabarEvents]);

  if (isAuthenticated && loading) {
    return <DashboardClientSkeleton />;
  }

  if (isAuthenticated && error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="w-10 h-10 text-destructive" />
          <p className="text-foreground font-medium">Gagal memuat dashboard</p>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="hidden md:block space-y-6 max-w-7xl mx-auto pb-10">
          <GuestLoginBanner />
          <QuickActionGrid />
          <MMRLeaderboardCard refreshKey={refreshKey} />
        </div>
        <div className="md:hidden flex flex-col space-y-4 p-4 pb-5" data-testid="guest-home">
          {refreshing && (
            <div className="flex justify-center py-1">
              <Icon icon="mdi:loading" className="w-6 h-6 animate-spin text-[#FC480D]" />
            </div>
          )}
          <GuestLoginBanner />
          <MMRLeaderboardCard refreshKey={refreshKey} />
          <QuickActionGrid />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Desktop and Tablet Layout */}
      <div className="hidden md:block space-y-6 max-w-7xl mx-auto pb-10">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Selamat Datang, {user?.name || localStorage.getItem('clientName') || 'Sportizen'}! 👋
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Pantau agenda, pembayaran, performa MMR, dan aktivitas olahragamu di sini.
          </p>
        </div>

        <UnpaidPaymentCard items={allUnpaidItems} />
        <AgendaTerdekatCard event={upcomingData} />
        <QuickActionGrid />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-7 space-y-6">
            <RaporEventCard
              userDisplayName={user?.name}
              userAvatar={user?.avatar}
              refreshKey={refreshKey}
            />
            <MabarTerdekatList refreshKey={refreshKey} />
          </div>
          <div className="lg:col-span-5 space-y-6">
            <MMRLeaderboardCard refreshKey={refreshKey} />
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden flex flex-col space-y-4 p-4 pb-5">
        {refreshing && (
          <div className="flex justify-center py-1">
            <Icon icon="mdi:loading" className="w-6 h-6 animate-spin text-[#FC480D]" />
          </div>
        )}
        <AgendaTerdekatCard event={upcomingData} />
        <UnpaidPaymentCard items={allUnpaidItems} />
        <MMRLeaderboardCard refreshKey={refreshKey} />
        <RaporEventCard userDisplayName={user?.name} userAvatar={user?.avatar} refreshKey={refreshKey} />
        <QuickActionGrid />
        <MabarTerdekatList refreshKey={refreshKey} />
      </div>
    </>
  );
};

export default ClientDashboard;
